(() => {
  'use strict';

  const VERSION = 'CANFOTO-E1-FINAL1-R2J-C-QA-SELECTOR-REMOVAL-R1';
  const STORAGE_KEY = 'canfoto_experience_theme_v1';
  const QA_STORAGE_KEY = 'canfoto_experience_qa_v1';
  const root = document.documentElement;
  const fallback = root.getAttribute('data-cf-theme') || 'cf-cinema';

  const registry = Object.freeze([
    Object.freeze({ id: 'cf-editorial', name: 'Editorial Conversion', mode: 'light' }),
    Object.freeze({ id: 'cf-magazine', name: 'Magazine Commerce', mode: 'light' }),
    Object.freeze({ id: 'cf-nature', name: 'Nature Distilled Studio', mode: 'light' }),
    Object.freeze({ id: 'cf-newsroom', name: 'Raw Newsroom Commerce', mode: 'light' }),
    Object.freeze({ id: 'cf-retrofuture', name: 'Retrofuture Signal Studio', mode: 'dark' }),
    Object.freeze({ id: 'cf-redaxis', name: 'Red Axis Manifesto', mode: 'dark' }),
    Object.freeze({ id: 'cf-chrome', name: 'Chrome Frame Future', mode: 'dark' }),
    Object.freeze({ id: 'cf-cinema', name: 'Cinema Light Dossier', mode: 'dark' }),
    Object.freeze({ id: 'cf-hyperpop', name: 'Hyperpop Grid', mode: 'dark' })
  ]);

  const allowed = new Set(registry.map((profile) => profile.id));
  const aliases = Object.freeze({
    editorial: 'cf-editorial',
    magazine: 'cf-magazine',
    nature: 'cf-nature',
    newsroom: 'cf-newsroom',
    retrofuture: 'cf-retrofuture',
    redaxis: 'cf-redaxis',
    chrome: 'cf-chrome',
    cinema: 'cf-cinema',
    hyperpop: 'cf-hyperpop'
  });

  const canonicalAtBoot = document.querySelector('link[rel="canonical"]')?.href || '';
  const locationAtBoot = window.location.href;

  function safeStorageGet(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (_) {
      return null;
    }
  }

  function safeStorageSet(key, value) {
    try {
      window.localStorage.setItem(key, value);
      return true;
    } catch (_) {
      return false;
    }
  }

  function safeStorageRemove(key) {
    try {
      window.localStorage.removeItem(key);
      return true;
    } catch (_) {
      return false;
    }
  }

  function normalizeProfile(value) {
    const raw = String(value || '').trim();
    if (allowed.has(raw)) return raw;
    if (aliases[raw] && allowed.has(aliases[raw])) return aliases[raw];
    return allowed.has(fallback) ? fallback : registry[0].id;
  }

  function currentProfile() {
    return normalizeProfile(root.getAttribute('data-cf-theme'));
  }

  function syncQaControls(profileId) {
    document.querySelectorAll('[data-cf-experience-select]').forEach((select) => {
      if (select.value !== profileId) select.value = profileId;
    });
    document.querySelectorAll('[data-cf-experience-status]').forEach((status) => {
      const match = registry.find((item) => item.id === profileId);
      status.textContent = match ? `${match.name} · ${match.mode === 'dark' ? 'Koyu' : 'Açık'}` : profileId;
    });
  }

  function setProfile(value, options = {}) {
    const profileId = normalizeProfile(value);
    const previous = currentProfile();
    const persist = options.persist !== false;
    const source = String(options.source || 'api');

    root.setAttribute('data-cf-theme', profileId);
    if (persist) safeStorageSet(STORAGE_KEY, profileId);
    syncQaControls(profileId);

    if (profileId !== previous) {
      window.dispatchEvent(new CustomEvent('canfoto:experience-change', {
        detail: Object.freeze({
          version: VERSION,
          previous,
          current: profileId,
          source
        })
      }));
    }

    return profileId;
  }

  function getProfile() {
    return currentProfile();
  }

  function listProfiles() {
    return registry.map((item) => ({ ...item }));
  }

  function resetProfile() {
    safeStorageRemove(STORAGE_KEY);
    return setProfile(fallback, { persist: false, source: 'reset' });
  }

  function shouldMountQaSelector() {
    // FINAL1-R2J-C: the visible storefront QA selector is permanently retired.
    // Keep the 9-style API and persistence; clear only the obsolete QA mount flag.
    safeStorageRemove(QA_STORAGE_KEY);
    return false;
  }

  function mountQaSelector() {
    // Fail closed even if an older cached/injected panel exists in the DOM.
    document.querySelectorAll('[data-cf-experience-qa], .cf-experience-qa').forEach((node) => node.remove());
    shouldMountQaSelector();
  }

  const api = Object.freeze({
    version: VERSION,
    storageKey: STORAGE_KEY,
    fallback,
    get: getProfile,
    set: setProfile,
    reset: resetProfile,
    list: listProfiles
  });

  window.CanFotoExperience = api;
  root.setAttribute('data-cf-experience-core', 'ready');

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountQaSelector, { once: true });
  } else {
    mountQaSelector();
  }

  window.debugCanFotoExperienceCore = function debugCanFotoExperienceCore() {
    const profile = getProfile();
    const stored = safeStorageGet(STORAGE_KEY);
    const canonicalNow = document.querySelector('link[rel="canonical"]')?.href || '';
    const result = {
      ok: true,
      version: VERSION,
      profileCount: registry.length,
      currentProfile: profile,
      validCurrentProfile: allowed.has(profile),
      storedProfile: stored,
      storedProfileValidOrEmpty: stored === null || allowed.has(stored),
      rootAttributeCount: document.querySelectorAll('html[data-cf-theme]').length,
      canonicalUnchanged: canonicalNow === canonicalAtBoot,
      locationUnchanged: window.location.href === locationAtBoot,
      qaSelectorCount: document.querySelectorAll('[data-cf-experience-qa]').length,
      reducedMotion: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || false
    };

    result.ok = [
      result.profileCount === 9,
      result.validCurrentProfile,
      result.storedProfileValidOrEmpty,
      result.rootAttributeCount === 1,
      result.canonicalUnchanged,
      result.locationUnchanged,
      result.qaSelectorCount === 0
    ].every(Boolean);

    return result;
  };
})();
